import { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import {
  BookingConnection,
  BookingState,
  BookingItem,
  TicketState,
} from 'types/api';
import { callWebviewHandler } from 'utils/webview';
import { WebviewOperatorType } from 'utils/webview';
import { isWebview } from 'utils/webview';
import { getEnvConstant } from 'constants/env';

import BookingUserCardV2 from '../components/BookingUserCard.v2';
import { SALE_DETAIL_PATH, SALE_LIST_PATH } from 'constants/path';
import ListLoadingSpinner from '../components/ListLoadingSpinner';
import EmptyList from '../components/EmptyList';
import SearchInput from 'components/SearchInput/SearchInput';

import MultipleBookingUserCardV2 from '../components/MultipleBookingUserCard.v2';
import { useIsMobile } from 'hooks/common';

const InputContainer = styled.div`
  width: 100%;

  padding: 0 20px;
  margin-bottom: 24px;
`;

const UserListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  padding: 0 20px 64px;

  > p {
    margin-bottom: 4px;

    font-weight: 600;
    font-size: 15px;
    line-height: 22px;

    color: rgba(70, 71, 76, 1);

    &.colored {
      color: rgba(0, 113, 233, 1);
    }
  }
`;

interface Props {
  bookingItems: BookingConnection;
  searchedKeyword: string;
  searchKeyword: string;
  keywordHandler: (value: string) => void;
  onSearch: () => void;
  needToCheckAttendance?: boolean;
  bookingItemsLoading: boolean;
  ticketType: 'fixed' | 'unfixed';
  totalCount?: number;
  handleRefetchBookingItemList: () => void;
  startTime: number;
  handleCancelBookingConfirmModal: () => void;
  saveSelectedConfirmedBookingIds: (
    selectedConfirmedBookingIds: string[],
  ) => void;
  ticketOriginType: 'fixed' | 'unfixed';
}

function SellingDetailSection({
  bookingItems,
  searchedKeyword,
  searchKeyword,
  keywordHandler,
  onSearch,
  needToCheckAttendance = false,
  bookingItemsLoading,
  ticketType,
  totalCount,
  handleRefetchBookingItemList,
  startTime,
  handleCancelBookingConfirmModal,
  saveSelectedConfirmedBookingIds,
  ticketOriginType,
}: Props) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const userList = bookingItems?.edges || [];

  const realUserList = userList.map(item => {
    const filteredItem = item.node.items.filter(bookingItem =>
      bookingItem.tickets.some(
        ticket => ticket?.scheduleTerm?.startedAt === startTime,
      ),
    );

    return {
      ...item,
      node: {
        ...item.node,
        items: filteredItem,
      },
    };
  });

  const sumUpFixedTotalCount = realUserList.reduce((acc, cur) => {
    const isCanceled = cur.node.status === BookingState.CANCELED;

    if (isCanceled) {
      return acc;
    }

    if (cur.node.counts.applied === 0 && cur.node.counts.total > 0) {
      return acc + cur.node.counts.confirmed;
    }

    return acc + cur.node.counts.applied;
  }, 0);

  const sumUpUnfixedTotalCount = realUserList.reduce((acc, cur) => {
    const isCanceled = cur.node.status === BookingState.CANCELED;

    if (isCanceled) {
      return acc;
    }

    const isMultipleTicket = cur.node.items[0].tickets.length > 1;

    if (isMultipleTicket) {
      let ticketCount = 0;

      cur.node.items.forEach(item => {
        item.tickets.forEach(ticket => {
          if (
            ticket.scheduleTerm?.startedAt === startTime &&
            ticket.status === TicketState.CONFIRMED
          ) {
            ticketCount += 1;
          }
        });
      });

      return acc + ticketCount;
    }

    return acc + cur.node.counts.confirmed;
  }, 0);

  const handleShowUserCardDetail = (itemId: string) => {
    setSelectedItemId(pre => {
      if (itemId === pre) {
        return null;
      }

      return itemId;
    });
  };

  const goToSalesPage = (orderId: string) => {
    if (isMobile) {
      navigate(`${SALE_DETAIL_PATH}/${orderId}`);
    } else {
      localStorage.setItem('orderId', orderId);
      navigate(SALE_LIST_PATH);
    }
  };

  const goToMessagePage = (orderId: string) => {
    if (isWebview()) {
      callWebviewHandler(WebviewOperatorType.OpenActionUrl, {
        actionUrl: `frip://chattingDetail/O${orderId}`,
      });

      return;
    }

    window.open(`${getEnvConstant('webClientUrl')}/messageDetail/O${orderId}`);
  };

  return (
    <>
      {bookingItemsLoading ? (
        <ListLoadingSpinner />
      ) : (
        <>
          <InputContainer>
            <SearchInput
              searchKeyword={searchKeyword}
              keywordHandler={keywordHandler}
              handleSearch={onSearch}
              placeholder="크루명이나 번호를 입력해 주세요."
              isSearched={
                searchKeyword !== '' && searchKeyword === searchedKeyword
              }
            />
          </InputContainer>

          <UserListBox>
            {isMobile && ticketType === 'fixed' ? (
              <></>
            ) : (
              <p className={searchedKeyword ? 'colored' : ''}>
                {searchedKeyword
                  ? `검색적용: ${
                      ticketType === 'unfixed'
                        ? sumUpUnfixedTotalCount
                        : sumUpFixedTotalCount
                    }건 `
                  : ticketType === 'unfixed'
                  ? sumUpUnfixedTotalCount > 1
                    ? `총 예약확정: ${sumUpUnfixedTotalCount}건`
                    : ''
                  : realUserList.some(
                      item => item.node.status === BookingState.CONFIRMED,
                    )
                  ? sumUpUnfixedTotalCount > 1
                    ? `총 예약확정: ${sumUpUnfixedTotalCount}건`
                    : ''
                  : sumUpFixedTotalCount > 1
                  ? `총 예약대기: ${sumUpFixedTotalCount}건`
                  : ''}
              </p>
            )}

            <>
              {realUserList.filter(
                item => item.node.status !== BookingState.CANCELED,
              ).length === 0 ? (
                <EmptyList />
              ) : (
                realUserList.map(({ node: data }, idx) => {
                  const isLast = realUserList?.length - 1 === idx;
                  const isSelectedUser = data?.id === selectedItemId;

                  const isCanceled = data?.status === BookingState.CANCELED;

                  const isMultipleTicket = data.items[0].tickets.length > 1;
                  // const isMultipleTicket = data.tickets?.length > 1;

                  return (
                    !isCanceled &&
                    (isMultipleTicket ? (
                      <MultipleBookingUserCardV2
                        key={`booking-user-list-${idx}-${data.id}`}
                        data={data}
                        isSelectedUser={isSelectedUser}
                        handleShowUserCardDetail={handleShowUserCardDetail}
                        idx={idx}
                        selectedItemId={selectedItemId}
                        goToSalesPage={goToSalesPage}
                        goToMessagePage={goToMessagePage}
                        needToCheckAttendance={needToCheckAttendance}
                        startTime={startTime}
                        // checkUserHandler={checkUserHandler}
                        // checkedList={checkedList}
                        // onClickCancelAbsent={onClickCancelAbsent}
                        // onClickCancelAttendance={onClickCancelAttendance}
                        // timeline={timeline}
                        ticketType={ticketType}
                        handleCancelBookingConfirmModal={
                          handleCancelBookingConfirmModal
                        }
                        saveSelectedConfirmedBookingIds={
                          saveSelectedConfirmedBookingIds
                        }
                        ticketOriginType={ticketOriginType}
                      />
                    ) : (
                      <BookingUserCardV2
                        key={`booking-user-list-${idx}-${data.id}`}
                        data={data}
                        isSelectedUser={isSelectedUser}
                        handleShowUserCardDetail={handleShowUserCardDetail}
                        idx={idx}
                        selectedItemId={selectedItemId}
                        goToSalesPage={goToSalesPage}
                        goToMessagePage={goToMessagePage}
                        needToCheckAttendance={needToCheckAttendance}
                        startTime={startTime}
                        // handleRestoreTickets={handleRestoreTickets}
                        saveSelectedConfirmedBookingIds={
                          saveSelectedConfirmedBookingIds
                        }
                        handleRefetchBookingItemList={
                          handleRefetchBookingItemList
                        }
                        handleCancelBookingConfirmModal={
                          handleCancelBookingConfirmModal
                        }
                        ticketOriginType={ticketOriginType}
                        ticketType={ticketType}
                      />
                    ))
                  );
                })
              )}
            </>
          </UserListBox>
        </>
      )}
    </>
  );
}

export default SellingDetailSection;
